import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";

/**
 * v4.2.2 — Legacy Transition Banner (Cohesion Pass).
 *
 * A slim, calm sticky bar rendered at the top of any legacy page when
 * the operator arrived via the Operational Base sidebar. Makes the
 * shell-shift honest:
 *
 *   * tells the operator they're in the classic view,
 *   * names the module they're using,
 *   * offers a clear one-tap return to /operational-base.
 *
 * Activation: reads sessionStorage["ob.legacy_arrival"] which the
 * AdaptiveSidebar drops when an operator clicks a legacy nav item.
 * The marker persists across reloads in the same session — closing
 * the banner clears it.
 *
 * The component is render-once-per-session: if the operator dismisses
 * it, it stays dismissed until they re-enter from OB again.
 */
export const LegacyTransitionBanner = () => {
    const { t } = useTranslation();
    const [arrival, setArrival] = useState(null);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("ob.legacy_arrival");
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.from === "operational-base") {
                setArrival(parsed);
            }
        } catch (_e) {
            /* sessionStorage may be unavailable */
        }
    }, []);

    if (!arrival) return null;

    const moduleName = arrival.display_name || arrival.module || "";

    const dismiss = () => {
        try {
            sessionStorage.removeItem("ob.legacy_arrival");
        } catch (_e) { /* noop */ }
        setArrival(null);
    };

    return (
        <div
            role="region"
            aria-label={t("ob.legacy_banner.label")}
            data-testid="ob-legacy-banner"
            className="w-full bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-300"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 flex-wrap">
                <Link
                    to="/operational-base"
                    onClick={dismiss}
                    data-testid="ob-legacy-banner-return"
                    className="inline-flex items-center gap-1.5 text-xs font-medium hover:underline text-emerald-700 dark:text-emerald-300"
                >
                    <ArrowLeft size={12} strokeWidth={1.75} aria-hidden="true" />
                    {t("ob.legacy_banner.return")}
                </Link>
                <span
                    aria-hidden="true"
                    className="text-xs opacity-50"
                >
                    ·
                </span>
                <p
                    className="text-xs flex-1 min-w-0 text-emerald-700 dark:text-emerald-300"
                    data-testid="ob-legacy-banner-context"
                >
                    {moduleName
                        ? t("ob.legacy_banner.context_with_name", { name: moduleName })
                        : t("ob.legacy_banner.context_generic")}
                </p>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label={t("ob.legacy_banner.dismiss")}
                    data-testid="ob-legacy-banner-dismiss"
                    className="flex-shrink-0 inline-flex items-center justify-center rounded-full p-1 hover:bg-emerald-100 dark:hover:bg-emerald-400/10 transition text-emerald-700 dark:text-emerald-300"
                >
                    <X size={12} strokeWidth={1.75} />
                </button>
            </div>
        </div>
    );
};

export default LegacyTransitionBanner;
